import React, { useRef, useState } from "react";
import { motion } from "motion/react";
import { API_URL } from "@/env";
import { getCookie } from "@/lib/cookies";
import { showCustomToast } from "@/components/CustomToast";
import { useOutsideClick } from "@/hooks/useOutsideClick";

interface ConfirmDeleteRecordingLinkProps {
  recordingLink: {
    token: string;
    name: string;
    count: number;
  };
  setShowConfirmDelete: (show: boolean) => void;
  onDeleteSuccess: () => void;
}

const ConfirmDeleteRecordingLink: React.FC<ConfirmDeleteRecordingLinkProps> = ({
  recordingLink,
  setShowConfirmDelete,
  onDeleteSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const modalRef = useRef(null);
  const token = getCookie("token");

  const handleDelete = async () => {
    if (loading) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/recording-links/${recordingLink.token}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        showCustomToast("Recording link deleted", "check");
        setShowConfirmDelete(false);
        onDeleteSuccess();
      } else {
        showCustomToast("Couldn't delete recording link", "exclamation");
      }
    } catch (error) {
      console.error("Error deleting recording link:", error);
      showCustomToast("Couldn't delete recording link", "exclamation");
    } finally {
      setLoading(false);
    }
  };

  useOutsideClick(modalRef, () => {
    if (!loading) {
      setShowConfirmDelete(false);
    }
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="onest-md bg-gray-50/[0.72] backdrop-blur-md z-[100000] fixed top-0 left-0 w-screen h-screen flex items-center justify-center"
    >
      <div
        ref={modalRef}
        className="p-6 rounded-[24px] bg-gray-200 max-w-[480px] w-full"
      >
        <h4 className="text-[20px] leading-[28px]">
          Are you sure you want to delete "{recordingLink.name}"?
        </h4>
        <p className="text-[14px] leading-[20px] text-white/[0.32] mt-2">
          This action cannot be undone and will permanently delete the recording
          link and its reports.
        </p>
        <div className="flex gap-3 mt-[32px]">
          <button
            onClick={() => setShowConfirmDelete(false)}
            disabled={loading}
            className="flex-1 flex items-center justify-center text-[14px] leading-[20px] h-[44px] rounded-[14px] bg-white/[0.04] text-white hover:bg-white/[0.08] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={loading}
            className="flex-1 flex items-center text-[14px] leading-[20px] justify-center h-[44px] rounded-[14px] text-red-700 bg-red-alpha/[0.08] hover:bg-red-alpha/[0.16] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Deleting..." : "Yes, I'm sure"}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default ConfirmDeleteRecordingLink;
